import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { FormattedMessage } from 'react-intl';
import { reduxForm, Field, change as changeAction } from 'redux-form';

import { withTheme } from 'common/styling/theme';
import { FileField, LightBoxModal, LightBoxImage, SimpleModal } from 'common/components';
import { getWithdrawalProof } from 'backoffice/users/_redux/actions';

import { closeLightBox, openLightBox } from 'interface/user/_redux/actions';
import imageDownload from 'common/images/documentDownload.png';
import iconDocument from 'common/images/preview2.png';

import classNames from 'classnames';
import { uploadPaymentProof, deletePaymentProof } from '../../_redux';
import { staticStyles, getDynamicStyles } from './style';

const PaymentProofForm = ({
  paymentId,
  uploadPaymentProof,
  deletePaymentProof,
  // paymentProofUploaded,
  paymentProofUploadError,
  getWithdrawalProof,
  lightBoxCurrentImage,
  lightBoxImages,
  closeLightBox,
  openLightBox,
  isDeposit,
  withdrawalProof,
  theme,
  paymentProofStatus,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [confirmDeleteProof, setConfirmDeleteProof] = useState(false);
  const dispatch = useDispatch();
  const IE = window.navigator && window.navigator.msSaveOrOpenBlob;

  // 提现：始终拉取凭证 blob 回显。存款：仅在存在凭证（paymentProofStatus 非空）时拉取，
  // 复用同一 action（getWithdrawalProof 打到 GET /payments/request/:id/proof/，该端点对所有
  // 请求类型返回 proof_document，blob 存 state.backoffice.users.withdrawalProof）。
  // rejected 的凭证不展示（预览卡片隐藏、直接回到上传区），无需拉取 blob。
  useEffect(() => {
    if (isDeposit) {
      if (paymentProofStatus && paymentProofStatus !== 'rejected') getWithdrawalProof(paymentId);
      return;
    }
    getWithdrawalProof(paymentId);
  }, [getWithdrawalProof, isDeposit, paymentId, paymentProofStatus]);

  // 存款凭证预览卡片的 objectURL：用 memo + 卸载 revoke，避免像提现路径那样每次渲染
  // 重新 createObjectURL 泄漏（也避免污染全局 document 对象）。
  // 必须同时要求 paymentProofStatus 非空：state.backoffice.users.withdrawalProof 是全局状态，
  // 跨详情页导航会残留上一个请求的 blob，若当前页无凭证（status 为空）不能用 stale blob 显示。
  // rejected 的凭证不展示，不需要为其创建 objectURL。
  const depositProofUrl = useMemo(
    () =>
      isDeposit && paymentProofStatus && paymentProofStatus !== 'rejected' && withdrawalProof
        ? URL.createObjectURL(withdrawalProof)
        : null,
    [isDeposit, paymentProofStatus, withdrawalProof]
  );

  useEffect(
    () => () => {
      if (depositProofUrl) URL.revokeObjectURL(depositProofUrl);
    },
    [depositProofUrl]
  );

  // 凭证不存在（未上传 / 已删除 / 跨详情页无凭证）或被拒（rejected）时，清空 redux-form
  // file 字段的残留值。删除路径由 deletePaymentProofSaga 在状态置空前清（避免闪烁），这里兜底
  // 覆盖跨详情页导航与被拒重传：上个存款页上传的 file 值会随全局 form 留存，被拒后 FileField
  // 重新挂载（回到上传区）若带上旧文件，且 .FileField__dropzone-plus-active（display:none）
  // 会隐藏 "Drop your attachments here" 文案 —— 被拒文件等于还在展示。
  // 注意清成 null 而非 []：空数组是 truthy，仍会隐藏文案。dispatch 引用稳定，effect 只在
  // isDeposit / paymentProofStatus 变化时触发，不会在用户拖入文件（status 未变）时误清。
  useEffect(() => {
    if (isDeposit && (!paymentProofStatus || paymentProofStatus === 'rejected')) {
      dispatch(changeAction('paymentProof', 'file', null));
    }
  }, [dispatch, isDeposit, paymentProofStatus]);

  // const allowedToUploadDocumentsStatuses = ['notUploaded', 'rejected', 'enabled', 'unverificated'];
  const acceptedFormats = [
    'image/png',
    'image/jpeg',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  const handleAfterUpload = file => {
    uploadPaymentProof({ id: paymentId, file });
  };

  const imageExtensions = {
    'image/png': '.png',
    'image/jpeg': '.jpeg',
  };

  const documentExtensions = {
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  };

  const extensions = { ...imageExtensions, ...documentExtensions };

  // 提现凭证展示专用（每次渲染重建 objectURL，属既有写法）；存款凭证用上面的 depositProofUrl memo，
  // 不走这里——避免全局 document 污染和额外泄漏。
  if (withdrawalProof && !isDeposit) {
    document.url = URL.createObjectURL(withdrawalProof);
    document.name = `proof_withdrawal_${paymentId}${extensions[withdrawalProof.type]}`;
  }

  const downloadDocument = () => {
    window.navigator.msSaveOrOpenBlob(withdrawalProof, document.name);
  };

  const DocumentsDownloadBlock = () => {
    if (IE) {
      return (
        <button type="button" onClick={() => downloadDocument()}>
          <img src={imageDownload} alt="Uptrader" />
        </button>
      );
    }
    return (
      <a href={document.url} download={document.name}>
        <img src={imageDownload} alt="Uptrader" />
      </a>
    );
  };

  return (
    <form
      className={classNames('PaymentProofForm', {
        'PaymentProofForm--no-approve': !withdrawalProof && !isDeposit,
      })}>
      <div className="PaymentProofForm__caption">
        <h2>
          <FormattedMessage id="justPaymentProof">{txt => txt}</FormattedMessage>
        </h2>
        {isDeposit && (
          <p>
            <FormattedMessage id="paymentProofUploadText">{txt => txt}</FormattedMessage>
          </p>
        )}
      </div>

      <div className="PaymentProofForm__input-inner">
        {/* 未上传（paymentProofStatus 为空）或被拒（rejected）时都显示上传区：
            rejected 凭证不展示，直接回到 dropzone 让用户重新上传，与 KYC 面板一致。 */}
        {(paymentProofStatus === 'rejected' || !paymentProofStatus) && isDeposit && (
          <Field
            component={FileField}
            handleAfterUpload={handleAfterUpload}
            name="file"
            formatNameId="documentsFormats"
            fileNameId="justAttachments"
            accept={acceptedFormats.join(',')}
            multiple={false}
          />
        )}

        {paymentProofStatus === 'unverified' && isDeposit && (
          <div className="DocumentsForm__upload">
            <div className="DocumentsForm__upload-text">
              <FormattedMessage id="clientDocumentUpload">
                {txt => (
                  <p>
                    {txt}
                    <i />
                  </p>
                )}
              </FormattedMessage>
              <FormattedMessage id="clientDocumentUploadDescription" />
            </div>
          </div>
        )}

        {paymentProofStatus === 'verified' && (
          <FormattedMessage id="status_verified">
            {txt => (
              <p className="Document__status-verified">
                {txt}
                <i />
              </p>
            )}
          </FormattedMessage>
        )}
      </div>

      {/* 存款已上传凭证：以 KYC 缩略图卡片样式回显（图片→LightBox、PDF→新标签页原生预览、
          docx/.doc 等无法预览的格式→点击下载、verified→右上角绿色角标、unverified→右上角删除按钮）。
          删除规则与 KYC 一致：仅"待审核"(unverified) 凭证可删，已通过/被拒的不可删。
          rejected 凭证不展示（已回到上传区重新上传，旧文件对用户不可见）。
          再要求 paymentProofStatus 非空：防止跨详情页残留的 stale blob 在无凭证的存款页上误显示。 */}
      {withdrawalProof && isDeposit && paymentProofStatus && paymentProofStatus !== 'rejected' && (
        <div className="DocumentsLoadPanel__files">
          <div className="DocumentsLoadPanel__file">
            <div className="DocumentsLoadPanel__file-media">
              {withdrawalProof.type && withdrawalProof.type.startsWith('image/') ? (
                <FormattedMessage id="clientPreviewDocument">
                  {txt => (
                    <button
                      type="button"
                      className="DocumentsLoadPanel__file-thumb"
                      title={txt}
                      aria-label={txt}
                      onClick={() => openLightBox(1, [depositProofUrl])}>
                      <img src={depositProofUrl} alt={txt} />
                    </button>
                  )}
                </FormattedMessage>
              ) : (
                <a
                  className="DocumentsLoadPanel__file-nonimage"
                  href={depositProofUrl}
                  target="_blank"
                  rel="noopener noreferrer">
                  <img src={iconDocument} alt="" />
                </a>
              )}
            </div>

            {paymentProofStatus === 'verified' && (
              <FormattedMessage id="status_verified">
                {txt => (
                  <span className="DocumentsLoadPanel__file-verified" title={txt} aria-label={txt}>
                    <i />
                  </span>
                )}
              </FormattedMessage>
            )}

            {/* 仅"待审核"(unverified) 凭证显示删除按钮；删除需二次确认 */}
            {paymentProofStatus === 'unverified' && (
              <FormattedMessage id="clientDeleteDocument">
                {txt => (
                  <button
                    type="button"
                    className="DocumentsLoadPanel__file-delete"
                    title={txt}
                    aria-label={txt}
                    onClick={() => setConfirmDeleteProof(true)}>
                    <span aria-hidden="true">×</span>
                  </button>
                )}
              </FormattedMessage>
            )}
          </div>
        </div>
      )}

      {paymentProofUploadError && <span className="PaymentProofForm__error">{paymentProofUploadError}</span>}

      {withdrawalProof && !isDeposit && (
        <div className="PaymentProofForm__withdrawal-proof">
          {Object.keys(imageExtensions).includes(withdrawalProof.type) ? (
            <LightBoxImage
              className="UserDocument__lightBoxImage"
              key={1}
              openLightBox={() => openLightBox(1, [document.url])}
              image={document.url}
              titleId={document.name}
            />
          ) : (
            <div className="PaymentProofForm__withdrawal-proof-download">
              <DocumentsDownloadBlock />
              <FormattedMessage id="downloadDocument" />
            </div>
          )}
        </div>
      )}

      <LightBoxModal images={[lightBoxImages]} currentImage={lightBoxCurrentImage} onRequestClose={closeLightBox} />

      {confirmDeleteProof && (
        <SimpleModal
          caption={{ id: 'clientDeleteDocumentConfirm' }}
          content={{ id: 'clientDeleteDocumentConfirmText' }}
          closeHandler={() => setConfirmDeleteProof(false)}
          actionButton={{
            textId: 'justConfirm',
            handler: () => deletePaymentProof(paymentId),
          }}
          cancelButton={{ textId: 'justCancel' }}
        />
      )}

      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </form>
  );
};

PaymentProofForm.propTypes = {
  paymentId: PropTypes.number.isRequired,
  // paymentProofUploaded: PropTypes.bool.isRequired,
  uploadPaymentProof: PropTypes.func.isRequired,
  deletePaymentProof: PropTypes.func.isRequired,
  paymentProofUploadError: PropTypes.object,
  isDeposit: PropTypes.bool,
  theme: PropTypes.object,
  withdrawalProof: PropTypes.object,
  getWithdrawalProof: PropTypes.func.isRequired,
  lightBoxCurrentImage: PropTypes.number,
  lightBoxImages: PropTypes.array,
  closeLightBox: PropTypes.func,
  openLightBox: PropTypes.func,
  paymentProofStatus: PropTypes.string,
};

PaymentProofForm.defaultProps = {
  paymentProofUploadError: null,
  isDeposit: false,
  withdrawalProof: {},
  theme: {},
  closeLightBox: null,
  lightBoxCurrentImage: -1,
  lightBoxImages: [],
  openLightBox: null,
  paymentProofStatus: null,
};

export default compose(
  withTheme(),
  connect(
    state => ({
      // paymentProofUploaded: state.client.payments.paymentProofUploaded,
      paymentProofUploadError: state.client.payments.paymentProofUploadError,
      withdrawalProof: state.backoffice.users.withdrawalProof,
      lightBoxCurrentImage: state.user.lightBoxCurrentImage,
      lightBoxImages: state.user.lightBoxImages,
      paymentProofStatus: state.client.payments.paymentsInfo.paymentProofStatus,
    }),
    {
      uploadPaymentProof: data => uploadPaymentProof.request(data),
      deletePaymentProof: id => deletePaymentProof.request(id),
      getWithdrawalProof: id => getWithdrawalProof.request(id),
      openLightBox: (id, lightBoxImages) => openLightBox(id, lightBoxImages),
      closeLightBox,
    }
  ),
  reduxForm({
    form: 'paymentProof',
  })
)(PaymentProofForm);
